import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CubeTransparentIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  ArrowTopRightOnSquareIcon,
  CodeBracketIcon,
  LockClosedIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const SmartContractsPage: React.FC = () => {
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(id);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const contracts = [
    {
      name: 'AuctionFactory',
      address: '0x1234567890123456789012345678901234567890',
      description: 'Factory contract for creating and managing all auction types',
      verified: true,
      audit: 'CertiK',
      features: ['Create auctions', 'Manage fees', 'Emergency pause']
    },
    {
      name: 'EnglishAuction',
      address: '0x2345678901234567890123456789012345678901',
      description: 'Implementation of ascending price auction mechanism',
      verified: true,
      audit: 'CertiK',
      features: ['Ascending bids', 'Time extension', 'Reserve price']
    },
    {
      name: 'DutchAuction',
      address: '0x3456789012345678901234567890123456789012',
      description: 'Implementation of descending price auction mechanism',
      verified: true,
      audit: 'CertiK',
      features: ['Descending price', 'Instant settlement', 'Price floor']
    },
    {
      name: 'VickreyAuction',
      address: '0x4567890123456789012345678901234567890123',
      description: 'Sealed-bid second-price auction implementation',
      verified: true,
      audit: 'CertiK',
      features: ['Sealed bids', 'Commit-reveal', 'Second-price winner']
    },
    {
      name: 'CandleAuction',
      address: '0x5678901234567890123456789012345678901234',
      description: 'Auction with random end time using Chainlink VRF',
      verified: true,
      audit: 'CertiK',
      features: ['Random end time', 'Anti-sniping', 'VRF integration']
    },
    {
      name: 'StakingPool',
      address: '0x6789012345678901234567890123456789012345',
      description: 'NDH token staking for tier benefits and rewards',
      verified: true,
      audit: 'CertiK',
      features: ['Token staking', 'Tier calculation', 'Reward distribution']
    },
    {
      name: 'NDHToken',
      address: '0x7890123456789012345678901234567890123456',
      description: 'NARDIHA ERC-20 governance and utility token',
      verified: true,
      audit: 'CertiK',
      features: ['ERC-20', 'Burnable', 'Governance delegation']
    },
    {
      name: 'Governor',
      address: '0x8901234567890123456789012345678901234567',
      description: 'DAO governance contract for proposal management',
      verified: true,
      audit: 'CertiK',
      features: ['Proposal creation', 'Voting', 'Timelock execution']
    },
    {
      name: 'Treasury',
      address: '0x9012345678901234567890123456789012345678',
      description: 'Multi-sig controlled treasury for platform funds',
      verified: true,
      audit: 'CertiK',
      features: ['Multi-sig (4/7)', 'Timelock', 'Fund management']
    }
  ];

  const securityFeatures = [
    {
      icon: ShieldCheckIcon,
      title: 'Multiple Audits',
      description: 'All contracts audited by CertiK and Trail of Bits'
    },
    {
      icon: LockClosedIcon,
      title: 'Timelock Controls',
      description: '48-hour delay on all critical contract upgrades'
    },
    {
      icon: ClockIcon,
      title: 'Emergency Pause',
      description: 'Circuit breaker mechanism for immediate threat response'
    },
    {
      icon: CodeBracketIcon,
      title: 'Open Source',
      description: 'All contract code publicly verified on Polygonscan'
    }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-dark-900 to-dark-950">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <CubeTransparentIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Smart Contracts
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-6">
              Verified, audited, and open-source contracts powering TEMPLUM
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="px-4 py-2 bg-green-500/20 text-green-400 rounded-full flex items-center gap-2">
                <ShieldCheckIcon className="w-4 h-4" />
                Audited by CertiK
              </span>
              <span className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-full">
                Polygon Mainnet
              </span>
              <span className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-full">
                Chain ID: 137
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-12 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-6">
              {securityFeatures.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className="text-center">
                    <Icon className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                    <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                    <p className="text-gray-400 text-sm">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contract List */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Deployed Contracts</h2>

            <div className="space-y-4">
              {contracts.map((contract, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-dark-800/50 rounded-xl border border-dark-700"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-xl font-semibold text-white">{contract.name}</h3>
                          {contract.verified && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                              Verified
                            </span>
                          )}
                          {contract.audit && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                              Audited
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm mt-1">{contract.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 p-3 bg-dark-900/50 rounded-lg mb-4">
                      <code className="text-primary-400 text-sm font-mono flex-1 truncate">
                        {contract.address}
                      </code>
                      <button
                        onClick={() => copyToClipboard(contract.address, contract.name)}
                        className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                        title="Copy address"
                      >
                        {copiedAddress === contract.name ? (
                          <CheckIcon className="w-4 h-4 text-green-400" />
                        ) : (
                          <DocumentDuplicateIcon className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                      <a
                        href={`https://polygonscan.com/address/${contract.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                        title="View on Polygonscan"
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-400" />
                      </a>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {contract.features.map((feature, fidx) => (
                        <span
                          key={fidx}
                          className="px-3 py-1 bg-dark-700 text-gray-400 text-xs rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integration Example */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Integration Example</h2>
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <p className="text-gray-300 mb-4">
                Here's how to interact with TEMPLUM contracts using ethers.js:
              </p>
              <div className="bg-dark-900 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                <pre className="text-gray-300">{`import { ethers } from 'ethers';
import AuctionFactoryABI from './abis/AuctionFactory.json';

// Connect to Polygon
const provider = new ethers.providers.JsonRpcProvider(
  'https://polygon-rpc.com'
);

// Contract address
const AUCTION_FACTORY = '0x1234567890123456789012345678901234567890';

// Initialize contract
const auctionFactory = new ethers.Contract(
  AUCTION_FACTORY,
  AuctionFactoryABI,
  provider
);

// Get all active auctions
const activeAuctions = await auctionFactory.getActiveAuctions();
console.log('Active auctions:', activeAuctions);

// For write operations, connect with signer
const signer = provider.getSigner();
const auctionWithSigner = auctionFactory.connect(signer);

// Place a bid (example for English auction)
const auctionId = '0x...';
const bidAmount = ethers.utils.parseEther('1.0');
const tx = await auctionWithSigner.placeBid(auctionId, { value: bidAmount });
await tx.wait();`}</pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABIs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Contract ABIs</h2>
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <p className="text-gray-300 mb-4">
                Download contract ABIs for integration:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <a
                  href="/abis/AuctionFactory.json"
                  download
                  className="p-4 bg-dark-900/50 rounded-lg hover:bg-dark-700 transition-colors text-center"
                >
                  <CodeBracketIcon className="w-8 h-8 text-primary-400 mx-auto mb-2" />
                  <span className="text-white text-sm">AuctionFactory.json</span>
                </a>
                <a
                  href="/abis/EnglishAuction.json"
                  download
                  className="p-4 bg-dark-900/50 rounded-lg hover:bg-dark-700 transition-colors text-center"
                >
                  <CodeBracketIcon className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                  <span className="text-white text-sm">EnglishAuction.json</span>
                </a>
                <a
                  href="/abis/StakingPool.json"
                  download
                  className="p-4 bg-dark-900/50 rounded-lg hover:bg-dark-700 transition-colors text-center"
                >
                  <CodeBracketIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <span className="text-white text-sm">StakingPool.json</span>
                </a>
              </div>
              <a
                href="/abis/all-contracts.zip"
                download
                className="block mt-4 text-center text-primary-400 hover:text-primary-300"
              >
                Download All ABIs (ZIP)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Audit Reports */}
      <section className="py-16 bg-gradient-to-r from-green-500/10 to-blue-500/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <ShieldCheckIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">
              Security Audits
            </h2>
            <p className="text-gray-300 mb-8">
              Our smart contracts have been thoroughly audited by leading security firms.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/audits/certik-audit-report.pdf"
                target="_blank"
                className="px-6 py-3 bg-dark-800 hover:bg-dark-700 text-white rounded-xl transition-colors flex items-center gap-2"
              >
                CertiK Audit Report
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </a>
              <a
                href="/audits/trailofbits-audit-report.pdf"
                target="_blank"
                className="px-6 py-3 bg-dark-800 hover:bg-dark-700 text-white rounded-xl transition-colors flex items-center gap-2"
              >
                Trail of Bits Report
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bug Bounty */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-dark-800/50 rounded-xl p-8 border border-dark-700">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-yellow-500/20 rounded-xl">
                  <ShieldCheckIcon className="w-8 h-8 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Bug Bounty Program</h2>
                  <p className="text-gray-300 mb-4">
                    Found a vulnerability? We offer rewards up to $100,000 for critical security issues.
                  </p>
                  <div className="grid md:grid-cols-4 gap-4 mb-6">
                    <div className="p-3 bg-dark-900/50 rounded-lg text-center">
                      <span className="text-red-400 font-bold">Critical</span>
                      <p className="text-gray-400 text-sm">$50k - $100k</p>
                    </div>
                    <div className="p-3 bg-dark-900/50 rounded-lg text-center">
                      <span className="text-orange-400 font-bold">High</span>
                      <p className="text-gray-400 text-sm">$10k - $50k</p>
                    </div>
                    <div className="p-3 bg-dark-900/50 rounded-lg text-center">
                      <span className="text-yellow-400 font-bold">Medium</span>
                      <p className="text-gray-400 text-sm">$5k - $10k</p>
                    </div>
                    <div className="p-3 bg-dark-900/50 rounded-lg text-center">
                      <span className="text-green-400 font-bold">Low</span>
                      <p className="text-gray-400 text-sm">$500 - $5k</p>
                    </div>
                  </div>
                  <a
                    href="mailto:security@templum.io"
                    className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300"
                  >
                    Report a Vulnerability →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SmartContractsPage;
